import { MarketAggregatePoint } from '@app/api/analytics/types';
import { VARIABLES } from '@app/components/common/constants';
import { ChartDataPoint } from '../types';

/**
 * Get variable value from market aggregate point with unit conversion
 */
export function getVariableValue(point: MarketAggregatePoint, variableId: string): number {
  const variable = VARIABLES.find((v) => v.id === variableId);
  if (!variable) return 0;

  let value = point[variable.juaId] as number;

  // Convert temperature from Kelvin to Celsius if needed
  if (variable.id === '2t' && value > 100) {
    value = value - 273.15; // Kelvin to Celsius
  }

  // Convert solar radiation from J/m² to kJ/m² for better readability
  if (variable.id === 'ssrd') {
    value = value / 1000; // Joules to kilojoules
  }

  return value || 0;
}

/**
 * Transform raw run data into chart format
 * Note: Keeps original UTC timestamps - timezone formatting handled in UI components
 */
export function transformToChartData(
  allRunsData: Array<{
    modelId: string;
    initTime: string;
    label: string;
    data: MarketAggregatePoint[];
  }>,
  apiVariableId: string,
): ChartDataPoint[] {
  const timePointsMap = new Map<string, ChartDataPoint>();

  // Ensure server-provided times are treated as UTC and consistently formatted with 'Z'
  const normalizeUtcTime = (t: string): string => {
    // If time already has 'Z' suffix, leave as is
    if (/Z$/i.test(t)) {
      return t;
    }
    // If time has +00:00 or +0000, replace with 'Z'
    if (/\+00:?00$/.test(t)) {
      return t.replace(/\+00:?00$/, 'Z');
    }
    // If time has other timezone offset, convert to UTC with Z
    const offsetMatch = t.match(/([+-]\d{2}):?(\d{2})$/);
    if (offsetMatch) {
      // Parse and convert to UTC, then format with Z
      const date = new Date(t);
      return date.toISOString();
    }
    // If it looks like ISO without timezone (YYYY-MM-DDTHH:mm or with seconds), append 'Z'
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?$/.test(t)) {
      return `${t}Z`;
    }
    // Fallback: append Z
    return `${t}Z`;
  };

  allRunsData.forEach((runData) => {
    runData.data.forEach((point) => {
      const rawTime = point.valid_time;
      const time = normalizeUtcTime(rawTime);

      if (!timePointsMap.has(time)) {
        timePointsMap.set(time, { time });
      }

      const chartPoint = timePointsMap.get(time)!;
      const value = getVariableValue(point, apiVariableId);
      chartPoint[runData.label] = value;
    });
  });

  // Convert map to array and sort by time
  return Array.from(timePointsMap.values()).sort((a, b) => Date.parse(a.time) - Date.parse(b.time));
}

/**
 * Calculate statistical measures for multiple runs
 * Only computes mean/std at time points where ALL runs have data
 * This handles models with different temporal resolutions (1h, 3h, 6h)
 */
export function addStatisticalMeasures(
  chartData: ChartDataPoint[],
  totalRuns: number,
  runLabels?: string[],
): void {
  if (totalRuns <= 1) return;

  // Helper to check if a value is a valid number (not undefined, null, NaN, or Infinity)
  const isValidNumber = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value);

  if (runLabels && runLabels.length > 0) {
    // Step 1: Find max time for each model (the last time point where it has VALID data)
    const modelMaxTimes = new Map<string, number>();
    runLabels.forEach((label) => {
      let maxTime = 0;
      chartData.forEach((dataPoint) => {
        const value = dataPoint[label];
        if (isValidNumber(value)) {
          const time = Date.parse(dataPoint.time);
          if (time > maxTime) {
            maxTime = time;
          }
        }
      });
      modelMaxTimes.set(label, maxTime);
    });

    // Step 2: For each time point, compute mean only from models that are still "active"
    // (models whose max_time >= current time), and only if ALL active models have data
    chartData.forEach((dataPoint) => {
      const currentTime = Date.parse(dataPoint.time);

      // Get labels for models that are still active at this time
      const activeLabels = runLabels.filter((label) => {
        const maxTime = modelMaxTimes.get(label) || 0;
        return maxTime >= currentTime;
      });

      // Need at least 2 active models for a meaningful mean
      if (activeLabels.length < 2) {
        return;
      }

      // Check if ALL active models have data at this time point
      const allActiveHaveData = activeLabels.every((label) => isValidNumber(dataPoint[label]));

      if (!allActiveHaveData) {
        return; // Not all active models have data - skip (resolution mismatch)
      }

      // Compute mean from active models only
      const values = activeLabels.map((label) => dataPoint[label] as number);

      if (values.length > 0) {
        const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
        const variance =
          values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
        const std = Math.sqrt(variance);

        dataPoint['Mean'] = mean;
        dataPoint['Mean + Std'] = mean + std;
        dataPoint['Mean - Std'] = mean - std;
      }
    });
  } else {
    // Fallback to old behavior if no labels provided
    chartData.forEach((dataPoint) => {
      const values: number[] = [];
      Object.keys(dataPoint).forEach((key) => {
        if (key !== 'time' && typeof dataPoint[key] === 'number') {
          values.push(dataPoint[key] as number);
        }
      });

      if (values.length > 0) {
        const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
        const variance =
          values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
        const std = Math.sqrt(variance);

        dataPoint['Mean'] = mean;
        dataPoint['Mean + Std'] = mean + std;
        dataPoint['Mean - Std'] = mean - std;
      }
    });
  }
}

/**
 * Convert absolute chart data to delta format (difference from specified baseline)
 * Note: This function filters out statistical measures (Mean, Mean + Std, Mean - Std)
 * as they are not meaningful in delta mode
 */
export function convertToDeltas(
  chartData: ChartDataPoint[],
  runLabels: string[],
  customBaseline?: string,
): ChartDataPoint[] {
  if (runLabels.length <= 1) return [];

  // Filter out statistical measures from run labels - they shouldn't appear in delta mode
  const actualRunLabels = runLabels.filter(
    (label) => !['Mean', 'Mean + Std', 'Mean - Std'].includes(label),
  );

  if (actualRunLabels.length <= 1) return [];

  // Use custom baseline if provided, otherwise default to first actual run
  const baselineLabel =
    customBaseline && actualRunLabels.includes(customBaseline)
      ? customBaseline
      : actualRunLabels[0];

  // All runs except the baseline become delta series
  const deltaLabels = actualRunLabels.filter((label) => label !== baselineLabel);

  return chartData.map((dataPoint) => {
    const deltaPoint: ChartDataPoint = { time: dataPoint.time };
    const baselineValue = dataPoint[baselineLabel] as number;

    if (baselineValue !== null && baselineValue !== undefined) {
      deltaLabels.forEach((label) => {
        const value = dataPoint[label] as number;
        if (value !== null && value !== undefined) {
          deltaPoint[label] = value - baselineValue;
        }
      });
    }

    return deltaPoint;
  });
}
